import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, MailOpen, Package, Globe2, Loader2 } from "lucide-react";
import { Logo } from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
    const [user, setUser] = useState(null);
    const [enquiries, setEnquiries] = useState(null);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        (async () => {
            try {
                let u = location.state?.user;
                if (!u) {
                    const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
                    u = data;
                }
                setUser(u);
                const res = await axios.get(`${API}/enquiries`, { withCredentials: true });
                setEnquiries(res.data);
            } catch {
                navigate("/login");
            }
        })();
    }, [navigate, location.state]);

    const logout = async () => {
        await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
        navigate("/login");
    };

    if (!user || enquiries === null) {
        return (
            <div className="min-h-screen bg-[#0F0C08] flex items-center justify-center" data-testid="admin-loading">
                <Loader2 size={26} className="animate-spin text-[#C58B49]" />
            </div>
        );
    }

    const samples = enquiries.filter((e) => e.sample_request).length;
    const regions = new Set(enquiries.map((e) => e.country)).size;

    return (
        <div data-testid="admin-dashboard" className="min-h-screen bg-[#0F0C08] grain relative">
            <header className="border-b border-[#2E2620] bg-[#14110F]/85 backdrop-blur-xl sticky top-0 z-40">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[76px] flex items-center justify-between">
                    <Logo className="h-11" />
                    <div className="flex items-center gap-5">
                        <span className="hidden sm:block text-xs tracking-[0.15em] uppercase text-[#8C8075]" data-testid="admin-user-email">
                            {user.email}
                        </span>
                        <button
                            data-testid="admin-logout-btn"
                            onClick={logout}
                            className="flex items-center gap-2 px-4 py-2 rounded-sm border border-[#2E2620] text-[12px] tracking-[0.12em] uppercase text-[#C2B7AD] hover:border-[#C58B49]/60 hover:text-[#D4A373] transition-colors"
                        >
                            <LogOut size={13} /> Sign Out
                        </button>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
                <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-3">Owner Dashboard</p>
                <h1 className="font-display text-4xl sm:text-5xl font-semibold text-[#F4EFEA] tracking-tight">
                    Wholesale <em className="italic text-[#D4A373]">enquiries</em>
                </h1>

                <div className="mt-10 grid grid-cols-3 gap-4" data-testid="admin-stats">
                    {[
                        { icon: MailOpen, label: "Total Enquiries", value: enquiries.length },
                        { icon: Package, label: "Sample Requests", value: samples },
                        { icon: Globe2, label: "Regions", value: regions },
                    ].map((s) => (
                        <div key={s.label} className="rounded-md border border-[#2E2620] bg-[#1D1814] p-5 sm:p-7">
                            <s.icon size={17} strokeWidth={1.6} className="text-[#C58B49]" />
                            <div className="mt-3 font-display text-3xl sm:text-4xl text-[#D4A373]">{s.value}</div>
                            <div className="mt-1 text-[10px] sm:text-xs tracking-[0.14em] uppercase text-[#8C8075]">{s.label}</div>
                        </div>
                    ))}
                </div>

                <div className="mt-10 rounded-md border border-[#2E2620] bg-[#1D1814] overflow-hidden" data-testid="enquiries-table">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left">
                            <thead>
                                <tr className="border-b border-[#2E2620]">
                                    {["Date", "Buyer", "Region", "Collections", "Qty / MOQ", "Sample", "Message"].map((h) => (
                                        <th key={h} className="px-5 py-4 font-mono2 text-[10px] tracking-[0.2em] uppercase text-[#8C8075] font-normal whitespace-nowrap">
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {enquiries.length === 0 && (
                                    <tr>
                                        <td colSpan={7} className="px-5 py-12 text-center text-sm text-[#8C8075]" data-testid="enquiries-empty">
                                            No enquiries yet — they will appear here the moment a buyer writes in.
                                        </td>
                                    </tr>
                                )}
                                {enquiries.map((e) => (
                                    <tr key={e.id} className="border-b border-[#2E2620]/60 hover:bg-[#14110F] transition-colors" data-testid={`enquiry-row-${e.id}`}>
                                        <td className="px-5 py-4 text-xs text-[#8C8075] whitespace-nowrap">
                                            {new Date(e.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                                        </td>
                                        <td className="px-5 py-4">
                                            <div className="text-sm text-[#F4EFEA]">{e.name}</div>
                                            <a href={`mailto:${e.email}`} className="text-xs text-[#D4A373] hover:underline">{e.email}</a>
                                            {e.company && <div className="text-xs text-[#8C8075] mt-0.5">{e.company}</div>}
                                        </td>
                                        <td className="px-5 py-4 text-xs text-[#C2B7AD] whitespace-nowrap">{e.country}</td>
                                        <td className="px-5 py-4 text-xs text-[#C2B7AD]">{e.categories.join(", ") || "—"}</td>
                                        <td className="px-5 py-4 text-xs text-[#C2B7AD] whitespace-nowrap">{e.quantity || "—"}</td>
                                        <td className="px-5 py-4">
                                            <span className={`text-[10px] tracking-[0.1em] uppercase px-2 py-1 rounded-sm border ${e.sample_request ? "border-[#C58B49]/60 text-[#D4A373]" : "border-[#2E2620] text-[#6E6156]"}`}>
                                                {e.sample_request ? "Yes" : "No"}
                                            </span>
                                        </td>
                                        <td className="px-5 py-4 text-xs text-[#8C8075] max-w-[240px]">{e.message}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>
        </div>
    );
}
